import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Info } from 'lucide-react';
import { Checkbox } from '../../../../components/ui/Checkbox';
import { ChoicePills, SectionTitle } from './ChoiceControls';
import { COLUMN_FAMILIES, isColumnOffered, matchingPreset, offeredPresets, presetColumns, type ColumnAccess, type ColumnKey, type ExportPreset } from './exportOptions';
import { columnLabel, familyLabel, presetHint, presetLabel } from './exportText';

interface Props {
  columns: ColumnKey[];
  access: ColumnAccess;
  onChange: (columns: ColumnKey[]) => void;
}

/** "What goes in the file": three starting points, then every column under them as its own tick box. */
const ColumnsSection: React.FC<Props> = ({ columns, access, onChange }) => {
  const { t } = useTranslation();
  const preset = matchingPreset(columns, access);
  const ticked = new Set(columns);

  const toggle = (key: ColumnKey) => {
    onChange(ticked.has(key) ? columns.filter((c) => c !== key) : [...columns, key]);
  };

  return (
    <section className="space-y-4 p-5" aria-labelledby="payroll-export-data">
      <SectionTitle id="payroll-export-data">{t('payrollExport.section.data')}</SectionTitle>
      <div>
        <ChoicePills<ExportPreset>
          name="payroll-export-preset"
          legend={t('payrollExport.preset.legend')}
          value={preset}
          choices={offeredPresets(access).map((value) => ({ value, label: presetLabel(value, t) }))}
          onChange={(value) => onChange(presetColumns(value, access))}
        />
        <p className="mt-2 text-xs text-muted-foreground" data-testid="payroll-export-preset-hint">{presetHint(preset, t)}</p>
      </div>
      <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
        {COLUMN_FAMILIES.map(({ family, columns: familyColumns }) => (
          <fieldset key={family} className="min-w-0">
            <legend className="mb-1.5 text-xs font-medium text-muted-foreground">{familyLabel(family, t)}</legend>
            <div className="space-y-0.5">
              {familyColumns
                .filter((key) => isColumnOffered(key, access))
                .map((key) => {
                  const id = `payroll-export-column-${key}`;
                  const locked = key === 'name';
                  return (
                    // A row label, not the Checkbox `label` prop: the prop's gap comes from space-x, which
                    // lands on the wrong side in Arabic. focus-within is the keyboard ring the box lacks.
                    <div key={key} className="flex items-center gap-2 rounded focus-within:ring-2 focus-within:ring-ring">
                      <Checkbox
                        id={id}
                        checked={locked || ticked.has(key)}
                        disabled={locked}
                        onChange={() => toggle(key)}
                        data-testid={id}
                      />
                      <label htmlFor={id} className={locked ? 'min-w-0 flex-1 py-1 text-sm text-muted-foreground' : 'min-w-0 flex-1 cursor-pointer py-1 text-sm text-foreground'}>
                        {columnLabel(key, t)}
                      </label>
                    </div>
                  );
                })}
            </div>
            {family === 'payment' && !access.bankDetailsAllowed && (
              <p className="mt-2 flex items-start gap-1.5 text-xs text-muted-foreground" data-testid="payroll-export-bank-off">
                <Info size={14} className="mt-px shrink-0" aria-hidden="true" />
                <span className="min-w-0">
                  {t(access.labourCardApplies ? 'payrollExport.bankOff.note' : 'payrollExport.bankOff.noteNoLabourCard')}
                  {' '}
                  <Link
                    to="/settings/payroll/exports"
                    className="rounded-sm font-medium text-primary underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    data-testid="payroll-export-bank-off-link"
                  >
                    {t('payrollExport.bankOff.link')}
                  </Link>
                </span>
              </p>
            )}
          </fieldset>
        ))}
      </div>
    </section>
  );
};

export default ColumnsSection;
